import { DGBETA_ENCRYPTION_KEY } from "src/config/env"

/**
 * Converts Base64URL to standard Base64 and adds proper padding
 * @param base64url - Base64URL encoded string
 * @returns Standard base64 string with proper padding
 */
const base64UrlToBase64 = (base64url: string): string => {
  // Replace Base64URL characters with standard base64 characters
  let base64 = base64url.replace(/-/g, "+").replace(/_/g, "/")

  // Add padding if needed
  const padding = base64.length % 4
  if (padding) {
    base64 += "=".repeat(4 - padding)
  }

  return base64
}

/**
 * Safely decodes Base64URL string with error handling
 * @param base64urlString - Base64URL encoded string
 * @returns Uint8Array or null if decoding fails
 */
const safeBase64UrlDecode = (base64urlString: string): Uint8Array | null => {
  try {
    const base64 = base64UrlToBase64(base64urlString)
    const binaryString = atob(base64)
    return Uint8Array.from(binaryString, c => c.charCodeAt(0))
  } catch (error) {
    console.error("Base64URL decoding failed:", error)
    return null
  }
}

/**
 * Decrypts AES-256-GCM encrypted email from DGBeta links
 * @param encryptedData - Base64URL encoded encrypted data (IV + encrypted email + auth tag)
 * @returns Decrypted email string or null if decryption fails
 */
export const decryptEmail = async (
  encryptedData: string
): Promise<string | null> => {
  try {
    if (!DGBETA_ENCRYPTION_KEY) {
      console.error("DGBETA_ENCRYPTION_KEY is not configured")
      return null
    }

    if (!encryptedData || encryptedData.trim() === "") {
      console.error("Encrypted email data is empty")
      return null
    }

    // Decode Base64URL encrypted data
    const encryptedBuffer = safeBase64UrlDecode(encryptedData.trim())
    if (!encryptedBuffer) {
      console.error("Failed to decode Base64URL encrypted data")
      return null
    }

    // Validate minimum length (IV: 12 bytes + at least some encrypted data + auth tag)
    if (encryptedBuffer.length < 29) {
      console.error("Encrypted data is too short to be valid AES-256-GCM data")
      return null
    }

    // Extract IV (first 12 bytes) and encrypted data (remaining bytes including auth tag)
    const iv = encryptedBuffer.slice(0, 12)
    const encryptedDataWithTag = encryptedBuffer.slice(12)

    // Decode the Base64 encryption key
    let keyData: Uint8Array
    try {
      const keyBase64 = base64UrlToBase64(DGBETA_ENCRYPTION_KEY)
      const keyBinaryString = atob(keyBase64)
      keyData = Uint8Array.from(keyBinaryString, c => c.charCodeAt(0))
    } catch (error) {
      console.log("DGBETA_ENCRYPTION_KEY", DGBETA_ENCRYPTION_KEY)
      console.error("Failed to decode Base64 encryption key:", error)
      return null
    }

    // Verify key length is exactly 32 bytes for AES-256
    if (keyData.length !== 32) {
      console.error(
        `AES-256-GCM requires a 32-byte key, got ${keyData.length} bytes`
      )
      return null
    }

    // Import the key for AES-256-GCM
    const key = await crypto.subtle.importKey(
      "raw",
      keyData.buffer as ArrayBuffer,
      { name: "AES-GCM", length: 256 },
      false,
      ["decrypt"]
    )

    // Decrypt the data using AES-256-GCM
    // The Web Crypto API expects the auth tag to be included in the data
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: iv,
        tagLength: 128, // 16 bytes = 128 bits (auth tag length)
      },
      key as CryptoKey,
      encryptedDataWithTag
    )

    // Convert decrypted buffer to string
    const decryptedEmail = new TextDecoder().decode(decryptedBuffer)

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(decryptedEmail)) {
      console.error(
        "Decrypted data is not a valid email format:",
        decryptedEmail
      )
      return null
    }

    return decryptedEmail
  } catch (error) {
    console.error("Email decryption failed:", error)
    return null
  }
}

export default decryptEmail
